// HR → Training Videos (mig 291). Library · Sessions · Reports.
//
// TWO RECORDS, NEVER ADDED TOGETHER (ruled 2026-09-17): an OPEN is a logged-in user clicking a
// video; ATTENDANCE is an employee in the room when HR showed it. Coverage counts attendance only,
// and the opens column sits beside it, never inside it.
//
// Every component with its own state lives at module scope (focus gate).
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../services/api';
import { HRC, fmtDate, Card, SectionTitle, SubTabs, Table, Th, Td, Btn, Modal, Empty, Loading, Field, input } from './hrkit';
import { CATEGORIES, categoryLabel, youtubeId, thumbUrl, Departments, Player } from './trainingKit';

const errOf = (e, fallback) => e?.response?.data?.error || fallback;
const today = () => new Date().toISOString().slice(0, 10);
const NO_DEPT = '__none__';

// ─────────── Library ───────────
function ItemForm({ item, departments, onClose, onSaved }) {
  const [f, setF] = useState(() => item ? {
    title: item.title, description: item.description || '', link: item.youtube_id, category: item.category,
    for_all_departments: item.for_all_departments, department_ids: (item.departments || []).map(d => d.id),
    sort_order: item.sort_order, is_active: item.is_active,
  } : { title: '', description: '', link: '', category: 'induction', for_all_departments: false, department_ids: [], sort_order: 0, is_active: true });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const vid = youtubeId(f.link);
  const toggleDept = (id) => setF(x => ({ ...x, department_ids: x.department_ids.includes(id) ? x.department_ids.filter(d => d !== id) : [...x.department_ids, id] }));

  const save = () => {
    setErr('');
    if (!f.title.trim()) return setErr('Title is required.');
    if (!vid) return setErr('Paste a YouTube link (youtube.com or youtu.be).');
    if (!f.for_all_departments && !f.department_ids.length) return setErr('Pick the departments this video is for, or tick "All departments".');
    setBusy(true);
    const body = { ...f, sort_order: Number(f.sort_order) || 0 };
    (item ? api.patch(`/hr/training/items/${item.id}`, body) : api.post('/hr/training/items', body))
      .then(onSaved).catch(e => setErr(errOf(e, 'Save failed'))).finally(() => setBusy(false));
  };

  return (
    <Modal title={item ? 'Edit training video' : 'Add training video'} onClose={onClose} width={720}>
      <div style={{ display: 'grid', gap: 12 }}>
        <Field label="YouTube link *">
          <input style={input} value={f.link} placeholder="https://youtu.be/…  (unlisted, on the company channel)"
            onChange={e => setF(x => ({ ...x, link: e.target.value }))} />
          <div style={{ fontSize: 11, marginTop: 4, color: f.link && !vid ? HRC.red : HRC.muted }}>
            {f.link ? (vid ? `Video ID ${vid}` : 'Not a YouTube link — it will be refused.') : 'Only the video ID is stored.'}
          </div>
        </Field>
        {vid && <div style={{ maxWidth: 420 }}><Player youtubeId={vid} title="Preview" /></div>}
        <Field label="Title *"><input style={input} value={f.title} maxLength={200} onChange={e => setF(x => ({ ...x, title: e.target.value }))} /></Field>
        <Field label="Description"><textarea style={{ ...input, minHeight: 60 }} value={f.description} onChange={e => setF(x => ({ ...x, description: e.target.value }))} /></Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 12 }}>
          <Field label="Category *">
            <select style={input} value={f.category} onChange={e => setF(x => ({ ...x, category: e.target.value }))}>
              {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
          <Field label="Order"><input type="number" style={input} value={f.sort_order} onChange={e => setF(x => ({ ...x, sort_order: e.target.value }))} /></Field>
        </div>
        <Field label="For which departments *">
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, marginBottom: 6 }}>
            <input type="checkbox" checked={f.for_all_departments} onChange={e => setF(x => ({ ...x, for_all_departments: e.target.checked }))} /> All departments
          </label>
          {!f.for_all_departments && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {departments.map(d => (
                <label key={d.id} style={{ display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 12, padding: '4px 8px',
                  border: `1px solid ${HRC.border}`, borderRadius: 6, background: f.department_ids.includes(d.id) ? HRC.violet : '#fff' }}>
                  <input type="checkbox" checked={f.department_ids.includes(d.id)} onChange={() => toggleDept(d.id)} /> {d.name}
                </label>
              ))}
            </div>
          )}
        </Field>
        {item && (
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
            <input type="checkbox" checked={f.is_active} onChange={e => setF(x => ({ ...x, is_active: e.target.checked }))} /> Active (shown on the staff Training page)
          </label>
        )}
        {err && <div style={{ color: HRC.red, fontSize: 13 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Btn>
        </div>
      </div>
    </Modal>
  );
}

function LibraryView({ departments }) {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);   // null | 'new' | item
  const [err, setErr] = useState('');
  const load = useCallback(() => {
    api.get('/hr/training/items').then(r => setItems(r.data.data || [])).catch(e => { setErr(errOf(e, 'Could not load')); setItems([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <SectionTitle right={<Btn size="sm" onClick={() => setEditing('new')}>+ Add video</Btn>}>Library</SectionTitle>
      <div style={{ fontSize: 12, color: HRC.muted, marginBottom: 10 }}>
        Unlisted videos on the company YouTube channel. Staff see every active video for this company on their Training page; the departments are shown as a label.
      </div>
      {err && <div style={{ color: HRC.red, fontSize: 13 }}>{err}</div>}
      {items === null ? <Loading /> : !items.length ? <Empty>No videos yet.</Empty> : (
        <Table>
          <thead><tr><Th></Th><Th>Title</Th><Th>Category</Th><Th>For</Th><Th>Status</Th><Th></Th></tr></thead>
          <tbody>{items.map(it => (
            <tr key={it.id} style={{ opacity: it.is_active ? 1 : 0.55 }}>
              <Td style={{ width: 96 }}><img src={thumbUrl(it.youtube_id)} alt="" style={{ width: 88, borderRadius: 4, display: 'block' }} /></Td>
              <Td><div style={{ fontWeight: 600 }}>{it.title}</div>{it.description && <div style={{ fontSize: 12, color: HRC.muted }}>{it.description}</div>}</Td>
              <Td>{categoryLabel(it.category)}</Td>
              <Td><Departments item={it} /></Td>
              <Td>{it.is_active ? 'Active' : 'Inactive'}</Td>
              <Td><Btn size="sm" variant="ghost" onClick={() => setEditing(it)}>Edit</Btn></Td>
            </tr>
          ))}</tbody>
        </Table>
      )}
      {editing && <ItemForm item={editing === 'new' ? null : editing} departments={departments}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </Card>
  );
}

// ─────────── Attendee picker ───────────
// Grouped by department, with employees who have NO department in their own group — otherwise
// they could never be marked present.
function AttendeePicker({ employees, selected, onChange }) {
  const [search, setSearch] = useState('');
  const groups = useMemo(() => {
    const s = search.trim().toLowerCase();
    const m = new Map();
    for (const e of employees) {
      if (s && !`${e.full_name} ${e.employee_code || ''}`.toLowerCase().includes(s)) continue;
      const k = e.department_id || NO_DEPT;
      if (!m.has(k)) m.set(k, { name: e.department_name || 'No department', list: [] });
      m.get(k).list.push(e);
    }
    return [...m.entries()].sort((a, b) => (a[0] === NO_DEPT) - (b[0] === NO_DEPT));
  }, [employees, search]);
  const set = new Set(selected);
  const toggle = (id) => onChange(set.has(id) ? selected.filter(x => x !== id) : [...selected, id]);
  const toggleGroup = (list, on) => {
    const ids = list.map(e => e.id);
    onChange(on ? [...new Set([...selected, ...ids])] : selected.filter(x => !ids.includes(x)));
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <input style={{ ...input, maxWidth: 260 }} placeholder="Search name or code" value={search} onChange={e => setSearch(e.target.value)} />
        <span style={{ fontSize: 12, color: HRC.muted }}>{selected.length} selected</span>
      </div>
      <div style={{ maxHeight: 320, overflowY: 'auto', border: `1px solid ${HRC.border}`, borderRadius: 8, padding: 8 }}>
        {!groups.length && <Empty>No employees match.</Empty>}
        {groups.map(([k, g]) => {
          const all = g.list.every(e => set.has(e.id));
          return (
            <div key={k} style={{ marginBottom: 10 }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, fontWeight: 700,
                color: k === NO_DEPT ? HRC.amber : HRC.navy, marginBottom: 4 }}>
                <input type="checkbox" checked={all} onChange={e => toggleGroup(g.list, e.target.checked)} />
                {g.name} ({g.list.length})
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2, paddingLeft: 20 }}>
                {g.list.map(e => (
                  <label key={e.id} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
                    <input type="checkbox" checked={set.has(e.id)} onChange={() => toggle(e.id)} />
                    {e.full_name}{e.employee_code ? <span style={{ color: HRC.muted }}> · {e.employee_code}</span> : null}
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────── Sessions ───────────
function SessionForm({ items, employees, onClose, onSaved }) {
  const [f, setF] = useState({ held_on: today(), trainer_mode: 'employee', conducted_by_employee_id: '', external_trainer: '', location: '', notes: '', item_ids: [], employee_ids: [] });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const toggleItem = (id) => setF(x => ({ ...x, item_ids: x.item_ids.includes(id) ? x.item_ids.filter(i => i !== id) : [...x.item_ids, id] }));
  const save = () => {
    setErr('');
    if (!f.item_ids.length) return setErr('Pick at least one video shown in the session.');
    const trainer = f.trainer_mode === 'employee'
      ? { conducted_by_employee_id: f.conducted_by_employee_id || null, external_trainer: null }
      : { conducted_by_employee_id: null, external_trainer: f.external_trainer };
    setBusy(true);
    api.post('/hr/training/sessions', { held_on: f.held_on, location: f.location, notes: f.notes, item_ids: f.item_ids, employee_ids: f.employee_ids, ...trainer })
      .then(onSaved).catch(e => setErr(errOf(e, 'Save failed'))).finally(() => setBusy(false));
  };
  const active = items.filter(i => i.is_active);
  return (
    <Modal title="Record a training session" onClose={onClose} width={820}>
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12 }}>
          <Field label="Held on *"><input type="date" style={input} value={f.held_on} onChange={e => setF(x => ({ ...x, held_on: e.target.value }))} /></Field>
          <Field label="Location"><input style={input} value={f.location} onChange={e => setF(x => ({ ...x, location: e.target.value }))} /></Field>
        </div>
        <Field label="Taken by *">
          <div style={{ display: 'flex', gap: 12, fontSize: 13, marginBottom: 6 }}>
            <label><input type="radio" checked={f.trainer_mode === 'employee'} onChange={() => setF(x => ({ ...x, trainer_mode: 'employee' }))} /> An employee</label>
            <label><input type="radio" checked={f.trainer_mode === 'external'} onChange={() => setF(x => ({ ...x, trainer_mode: 'external' }))} /> An outside trainer</label>
          </div>
          {f.trainer_mode === 'employee' ? (
            <select style={input} value={f.conducted_by_employee_id} onChange={e => setF(x => ({ ...x, conducted_by_employee_id: e.target.value }))}>
              <option value="">— select —</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.full_name}{e.department_name ? ` · ${e.department_name}` : ''}</option>)}
            </select>
          ) : (
            <input style={input} placeholder="Trainer's name and organisation" value={f.external_trainer} onChange={e => setF(x => ({ ...x, external_trainer: e.target.value }))} />
          )}
        </Field>
        <Field label="Videos shown *">
          {!active.length ? <div style={{ fontSize: 13, color: HRC.muted }}>Add a video to the library first.</div> : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {active.map(i => (
                <label key={i.id} style={{ display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 12, padding: '4px 8px',
                  border: `1px solid ${HRC.border}`, borderRadius: 6, background: f.item_ids.includes(i.id) ? HRC.violet : '#fff' }}>
                  <input type="checkbox" checked={f.item_ids.includes(i.id)} onChange={() => toggleItem(i.id)} /> {i.title}
                </label>
              ))}
            </div>
          )}
        </Field>
        <Field label="Present">
          <AttendeePicker employees={employees} selected={f.employee_ids} onChange={ids => setF(x => ({ ...x, employee_ids: ids }))} />
        </Field>
        <Field label="Notes"><textarea style={{ ...input, minHeight: 50 }} value={f.notes} onChange={e => setF(x => ({ ...x, notes: e.target.value }))} /></Field>
        {err && <div style={{ color: HRC.red, fontSize: 13 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save session'}</Btn>
        </div>
      </div>
    </Modal>
  );
}

function SessionDetail({ sessionId, employees, onClose, onChanged }) {
  const [s, setS] = useState(null);
  const [editing, setEditing] = useState(false);
  const [picked, setPicked] = useState([]);
  const [reason, setReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/hr/training/sessions/${sessionId}`).then(r => {
      setS(r.data.data);
      setPicked(r.data.data.attendees.filter(a => !a.removed_at).map(a => a.employee_id));
    }).catch(e => setErr(errOf(e, 'Could not load session')));
  }, [sessionId]);
  useEffect(() => { load(); }, [load]);

  const saveAttendees = () => {
    setErr(''); setBusy(true);
    api.put(`/hr/training/sessions/${sessionId}/attendees`, { employee_ids: picked })
      .then(() => { setEditing(false); load(); onChanged(); }).catch(e => setErr(errOf(e, 'Save failed'))).finally(() => setBusy(false));
  };
  const cancel = () => {
    setErr('');
    if (!reason.trim()) return setErr('Say why the session is cancelled.');
    setBusy(true);
    api.post(`/hr/training/sessions/${sessionId}/cancel`, { reason })
      .then(() => { setCancelling(false); load(); onChanged(); }).catch(e => setErr(errOf(e, 'Cancel failed'))).finally(() => setBusy(false));
  };

  const live = s ? s.attendees.filter(a => !a.removed_at) : [];
  const removed = s ? s.attendees.filter(a => a.removed_at) : [];
  return (
    <Modal title="Training session" onClose={onClose} width={820}>
      {!s ? (err ? <div style={{ color: HRC.red }}>{err}</div> : <Loading />) : (
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ fontSize: 13, lineHeight: 1.7 }}>
            <strong>{fmtDate(s.held_on)}</strong>{s.location ? ` · ${s.location}` : ''} · taken by {s.conducted_by_name || s.external_trainer}
            <div>Videos: {s.items.map(i => i.title).join(', ')}</div>
            {s.notes && <div style={{ color: HRC.muted }}>{s.notes}</div>}
            <div style={{ color: HRC.muted, fontSize: 12 }}>Recorded by {s.created_by_name || '—'}</div>
            {s.cancelled_at && <div style={{ color: HRC.red }}>Cancelled {fmtDate(s.cancelled_at)} by {s.cancelled_by_name || '—'}: {s.cancel_reason}</div>}
          </div>
          {editing ? (
            <>
              <AttendeePicker employees={employees} selected={picked} onChange={setPicked} />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Btn variant="ghost" onClick={() => { setEditing(false); load(); }}>Discard</Btn>
                <Btn onClick={saveAttendees} disabled={busy}>Save attendance</Btn>
              </div>
            </>
          ) : (
            <>
              <SectionTitle right={!s.cancelled_at && <Btn size="sm" variant="ghost" onClick={() => setEditing(true)}>Edit attendance</Btn>}>
                Present ({live.length})
              </SectionTitle>
              {!live.length ? <Empty>Nobody recorded.</Empty> : (
                <Table>
                  <thead><tr><Th>Employee</Th><Th>Department</Th><Th>Recorded</Th></tr></thead>
                  <tbody>{live.map(a => (
                    <tr key={a.id}><Td>{a.full_name} <span style={{ color: HRC.muted }}>{a.employee_code}</span></Td>
                      <Td>{a.department_name || <span style={{ color: HRC.amber }}>No department</span>}</Td>
                      <Td style={{ fontSize: 12, color: HRC.muted }}>{fmtDate(a.recorded_at)} · {a.recorded_by_name || '—'}</Td></tr>
                  ))}</tbody>
                </Table>
              )}
              {!!removed.length && (
                <div style={{ fontSize: 12, color: HRC.muted }}>
                  <strong>Removed from the list:</strong> {removed.map(a => `${a.full_name} (by ${a.removed_by_name || '—'}, ${fmtDate(a.removed_at)})`).join('; ')}
                </div>
              )}
            </>
          )}
          {!s.cancelled_at && !editing && (cancelling ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={input} placeholder="Why is this session cancelled?" value={reason} onChange={e => setReason(e.target.value)} />
              <Btn variant="danger" onClick={cancel} disabled={busy}>Cancel session</Btn>
              <Btn variant="ghost" onClick={() => setCancelling(false)}>Keep</Btn>
            </div>
          ) : (
            <div><Btn size="sm" variant="ghost" onClick={() => setCancelling(true)}>Cancel this session…</Btn></div>
          ))}
          {err && <div style={{ color: HRC.red, fontSize: 13 }}>{err}</div>}
        </div>
      )}
    </Modal>
  );
}

function SessionsView({ employees }) {
  const [sessions, setSessions] = useState(null);
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => {
    api.get('/hr/training/sessions').then(r => setSessions(r.data.data || [])).catch(() => setSessions([]));
    api.get('/hr/training/items').then(r => setItems(r.data.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <Card>
      <SectionTitle right={<Btn size="sm" onClick={() => setCreating(true)}>+ Record session</Btn>}>Sessions</SectionTitle>
      <div style={{ fontSize: 12, color: HRC.muted, marginBottom: 10 }}>
        A session is a person in a room — this is the training record for the shop floor, and the only thing coverage counts.
      </div>
      {sessions === null ? <Loading /> : !sessions.length ? <Empty>No sessions recorded yet.</Empty> : (
        <Table>
          <thead><tr><Th>Held on</Th><Th>Videos</Th><Th>Taken by</Th><Th>Present</Th><Th>Status</Th><Th></Th></tr></thead>
          <tbody>{sessions.map(s => (
            <tr key={s.id} style={{ opacity: s.cancelled_at ? 0.55 : 1 }}>
              <Td>{fmtDate(s.held_on)}</Td>
              <Td>{s.items.map(i => i.title).join(', ')}</Td>
              <Td>{s.conducted_by_name || s.external_trainer}</Td>
              <Td>{s.attendees}</Td>
              <Td>{s.cancelled_at ? 'Cancelled' : 'Held'}</Td>
              <Td><Btn size="sm" variant="ghost" onClick={() => setOpenId(s.id)}>Open</Btn></Td>
            </tr>
          ))}</tbody>
        </Table>
      )}
      {creating && <SessionForm items={items} employees={employees} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {openId && <SessionDetail sessionId={openId} employees={employees} onClose={() => setOpenId(null)} onChanged={load} />}
    </Card>
  );
}

// ─────────── Reports ───────────
function CoverageModal({ item, onClose }) {
  const [data, setData] = useState(null);
  const [opens, setOpens] = useState(null);
  useEffect(() => {
    api.get(`/hr/training/report/items/${item.id}/coverage`).then(r => setData(r.data.data)).catch(() => setData({ employees: [] }));
    api.get(`/hr/training/report/items/${item.id}/opens`).then(r => setOpens(r.data.data || [])).catch(() => setOpens([]));
  }, [item.id]);
  const groups = useMemo(() => {
    const m = new Map();
    for (const e of (data?.employees || [])) {
      const k = e.no_department ? NO_DEPT : (e.department_name || 'No department');
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(e);
    }
    return [...m.entries()];
  }, [data]);
  return (
    <Modal title={`Coverage — ${item.title}`} onClose={onClose} width={820}>
      {!data ? <Loading /> : (
        <div style={{ display: 'grid', gap: 14 }}>
          {groups.map(([k, list]) => (
            <div key={k}>
              <div style={{ fontWeight: 700, fontSize: 13, color: k === NO_DEPT ? HRC.amber : HRC.navy, marginBottom: 4 }}>
                {k === NO_DEPT ? 'No department — not counted in coverage until HR gives them one' : k}
                <span style={{ fontWeight: 400, color: HRC.muted }}> · {list.filter(e => e.attended).length} of {list.length} attended</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 2 }}>
                {list.map(e => (
                  <div key={e.id} style={{ fontSize: 12, color: e.attended ? HRC.green : HRC.red }}>
                    {e.attended ? '✓' : '✗'} {e.full_name}{e.attended && e.last_held_on ? <span style={{ color: HRC.muted }}> · {fmtDate(e.last_held_on)}</span> : null}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div style={{ borderTop: `1px solid ${HRC.border}`, paddingTop: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>Opened by logged-in users <span style={{ fontWeight: 400, color: HRC.muted }}>— clicks, not training</span></div>
            {opens === null ? <Loading /> : !opens.length ? <div style={{ fontSize: 12, color: HRC.muted }}>Nobody has opened it.</div> : (
              <div style={{ fontSize: 12 }}>{opens.map(o => `${o.name} (${o.opens}×, last ${fmtDate(o.last_opened)})`).join(' · ')}</div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function ReportsView() {
  const [items, setItems] = useState(null);
  const [people, setPeople] = useState(null);
  const [cover, setCover] = useState(null);
  useEffect(() => {
    api.get('/hr/training/report/items').then(r => setItems(r.data.data || [])).catch(() => setItems([]));
    api.get('/hr/training/report/employees').then(r => setPeople(r.data.data || [])).catch(() => setPeople([]));
  }, []);
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Card>
        <SectionTitle>By video</SectionTitle>
        <div style={{ fontSize: 12, color: HRC.muted, marginBottom: 10 }}>
          <strong>Coverage</strong> counts people present at a session that showed the video. <strong>Opens</strong> are clicks by logged-in users, reported beside it and never added in.
        </div>
        {items === null ? <Loading /> : !items.length ? <Empty>No videos yet.</Empty> : (
          <Table>
            <thead><tr><Th>Video</Th><Th>For</Th><Th>Sessions</Th><Th>Coverage</Th><Th>No dept</Th><Th>Opens</Th><Th></Th></tr></thead>
            <tbody>{items.map(i => (
              <tr key={i.id} style={{ opacity: i.is_active ? 1 : 0.55 }}>
                <Td><div style={{ fontWeight: 600 }}>{i.title}</div><div style={{ fontSize: 11, color: HRC.muted }}>{categoryLabel(i.category)}</div></Td>
                <Td style={{ fontSize: 12 }}>{i.for_all_departments ? 'All' : 'Selected'}</Td>
                <Td>{i.sessions}</Td>
                <Td><strong>{i.covered}</strong> of {i.audience}
                  {i.attended_outside_audience > 0 && <div style={{ fontSize: 11, color: HRC.muted }}>+{i.attended_outside_audience} attended outside its departments</div>}</Td>
                <Td style={{ color: i.no_department ? HRC.amber : HRC.muted }}>{i.no_department || '—'}</Td>
                <Td>{i.opens} <span style={{ fontSize: 11, color: HRC.muted }}>by {i.openers}</span></Td>
                <Td><Btn size="sm" variant="ghost" onClick={() => setCover(i)}>Who</Btn></Td>
              </tr>
            ))}</tbody>
          </Table>
        )}
      </Card>
      <Card>
        <SectionTitle>By employee</SectionTitle>
        {people === null ? <Loading /> : !people.length ? <Empty>No active employees.</Empty> : (
          <Table>
            <thead><tr><Th>Employee</Th><Th>Department</Th><Th>Sessions</Th><Th>Videos covered</Th><Th>Last session</Th></tr></thead>
            <tbody>{people.map(p => (
              <tr key={p.id}>
                <Td>{p.full_name} <span style={{ color: HRC.muted }}>{p.employee_code}</span></Td>
                <Td>{p.department_name || <span style={{ color: HRC.amber }}>No department</span>}</Td>
                <Td>{p.sessions}</Td><Td>{p.videos}</Td><Td>{p.last_held_on ? fmtDate(p.last_held_on) : '—'}</Td>
              </tr>
            ))}</tbody>
          </Table>
        )}
      </Card>
      {cover && <CoverageModal item={cover} onClose={() => setCover(null)} />}
    </div>
  );
}

export default function TrainingAdmin() {
  const [view, setView] = useState('library');
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  useEffect(() => {
    api.get('/hr/departments').then(r => setDepartments(r.data.data || [])).catch(() => {});
    api.get('/hr/training/employees').then(r => setEmployees(r.data.data || [])).catch(() => {});
  }, []);
  return (
    <div>
      <SubTabs active={view} onChange={setView}
        tabs={[{ key: 'library', label: '🎬 Library' }, { key: 'sessions', label: '👥 Sessions' }, { key: 'reports', label: '📈 Reports' }]} />
      {view === 'library' && <LibraryView departments={departments} />}
      {view === 'sessions' && <SessionsView employees={employees} />}
      {view === 'reports' && <ReportsView />}
    </div>
  );
}
